// src/pages/ComingSoon.tsx
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Book, Users, Compass, Sparkles, Music, Heart, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

const features = {
  meditation: {
    icon: Music,
    title: "Meditation Library",
    description:
      "A calm collection of short audios, curated YouTube sessions from trusted guides, and a “Basics · 4-Step Guidance” to build a gentle daily ritual. Press play, breathe, and close with one kind action. Everything is designed to be light, accessible, and respectful of your pace.",
  },
  mentor: {
    icon: Compass,
    title: "Mentor Deep Dive",
    description:
      "Connect with mentors, including the founder, for thoughtful answers or 1:1 sessions. Before each conversation, the app prepares a concise Gem Summary so we start deeper and save your time. Transparent pricing, kind boundaries, and privacy by design.",
  },
  community: {
    icon: Users,
    title: "Heart Community International",
    description:
      "A gentle space to share Gem Reports, weekly reflections, and support one another. Join live guided meditations on Zoom and, where possible, local meetups beginning in Kuala Lumpur and Singapore. Simple community guidelines keep the circle warm and safe.",
  },
  dimension: {
    icon: Sparkles,
    title: "5th Dimension Civilization",
    description:
      "A series of paid, cohort-based courses for those who feel called to go further, practices for presence, compassion, boundaries, and service. Learn with experienced teachers, integrate lessons into your daily Gems, and walk steadily toward a higher-frequency way of living.",
  },
};

export default function ComingSoon() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const feature = searchParams.get("feature") || "meditation";
  const config = features[feature as keyof typeof features] || features.meditation;
  const Icon = config.icon;

  return (
    <div className="container max-w-2xl mx-auto py-16 px-4">
      <Card className="shadow-[var(--shadow-soft)]">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-3xl">{t(`nav.${feature}`, { defaultValue: config.title })}</CardTitle>
          <CardDescription className="text-base mt-4">
            {t(`comingSoon.${feature}.desc`, { defaultValue: config.description })}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-center space-y-6">
          <div className="inline-block px-6 py-3 bg-primary/5 rounded-full">
            <p className="text-sm font-medium text-primary">{t("comingSoon.badge", { defaultValue: "Coming Soon ✨" })}</p>
          </div>

          <p className="text-muted-foreground">
            {t("comingSoon.message", {
              defaultValue:
                "We're crafting something special. Thank you for your patience as this space unfolds.",
            })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
